import { ChangeDetectionStrategy, Component, DestroyRef, inject, Input, OnChanges, OnInit, SimpleChanges, signal, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { Banner, BannerVariant } from '../banner/banner';
import { TabItem, Tabs } from '../tabs/tabs';
import { REQUOTE_REMARK_TABS, REQUOTE_TIMELINE_MOCK } from './requote-remark-card.constants';
import { RemarkTimelineItem, RequoteApiItem, RequoteItem, RequoteStatus } from './requote-remark-card.model';
import { MatAccordion, MatExpansionModule, MatExpansionPanel, MatExpansionPanelHeader, MatExpansionPanelTitle } from "@angular/material/expansion";
import { MatIconModule } from '@angular/material/icon';
import { AppInput } from '../app-input/app-input';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AppTooltipComponent } from '../app-tooltip/app-tooltip.component';
import { ApplicationReviewService } from '../../features/services/application-review.service';

@Component({
  selector: 'app-requote-remark-card',
  imports: [
    CommonModule,
    Tabs,
    Banner,
    MatExpansionModule,
    MatExpansionPanelHeader,
    MatAccordion,
    MatExpansionPanelTitle,
    MatExpansionPanel,
    MatIconModule,
    ReactiveFormsModule,
    AppInput,
    AppTooltipComponent
  ],
  templateUrl: './requote-remark-card.html',
  styleUrl: './requote-remark-card.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RequoteRemarkCard implements OnInit, OnChanges {
  @Input() leadInfoId: string = '';
  @Input() refreshKey: number = 0;

  private readonly applicationReviewService = inject(ApplicationReviewService);
  private readonly fb = inject(FormBuilder);
  private readonly destroyRef = inject(DestroyRef);

  readonly remarkTabs = computed<TabItem[]>(() => [
    {
      ...REQUOTE_REMARK_TABS[0],
      count: this.remarks().length,
    },
    {
      ...REQUOTE_REMARK_TABS[1],
      count: this.requotes().length,
    },
  ]);
  readonly selectedTabIndex = signal<number>(0);

  readonly remarks = signal<RemarkTimelineItem[]>([]);
  readonly requotes = signal<RequoteItem[]>([]);
  readonly searchQuery = signal<string>('');
  readonly isLoadingRemarks = signal<boolean>(false);

  readonly searchForm: FormGroup;

  readonly filteredRemarks = computed(() => {
    const allRemarks = this.remarks();
    const query = this.searchQuery().toLowerCase().trim();

    if (!query) {
      return allRemarks;
    }

    return allRemarks.filter(remark =>
      remark.remark.toLowerCase().includes(query) ||
      (remark.createdUserName?.toLowerCase().includes(query) || false)
    );
  });

  readonly filteredRequotes = computed(() => {
    const allRequotes = this.requotes();
    const query = this.searchQuery().toLowerCase().trim();

    if (!query) {
      return allRequotes;
    }

    return allRequotes.filter((requote) =>
      requote.requestId.toLowerCase().includes(query) ||
      requote.status.toLowerCase().includes(query) ||
      requote.history.some((item) =>
        (item.reason || '').toLowerCase().includes(query) ||
        (item.actorName || '').toLowerCase().includes(query)
      )
    );
  });

  constructor() {
    this.searchForm = this.fb.group({
      search: ['', [Validators.maxLength(500)]]
    });
  }

  ngOnInit(): void {
    this.setupSearchListener();
    this.loadCardData();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ((changes['leadInfoId'] && !changes['leadInfoId'].firstChange) || (changes['refreshKey'] && !changes['refreshKey'].firstChange)) {
      this.loadCardData();
    }
  }

  private loadCardData(): void {
    if (!this.leadInfoId) return;
    this.fetchRemarks();
    this.fetchRequotes();
  }

  private setupSearchListener(): void {
    this.searchForm.get('search')?.valueChanges
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(value => {
        this.searchQuery.set(value || '');
      });
  }

  private fetchRemarks(): void {
    if (!this.leadInfoId) return;

    this.isLoadingRemarks.set(true);
    this.applicationReviewService.getReviewRemarks(this.leadInfoId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response: any) => {
          this.isLoadingRemarks.set(false);
          const remarksData = (response?.body?.remarks || []).map((item: any) => ({
            ...item,
            createdOnDate: item?.createdOnDate || item?.createdOn || '',
          }));
          this.remarks.set(remarksData);
        },
        error: () => {
          this.isLoadingRemarks.set(false);
        }
      });
  }

  private fetchRequotes(): void {
    if (!this.leadInfoId) return;

    this.applicationReviewService
      .getReviewRequoteInfo(this.leadInfoId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response: any) => {
          const requoteBody = response?.body || [];
          this.requotes.set(this.mapRequoteResponse(requoteBody));
        },
        error: () => {
          this.requotes.set(REQUOTE_TIMELINE_MOCK);
        },
      });
  }

  private mapRequoteResponse(items: RequoteApiItem[]): RequoteItem[] {
    if (!Array.isArray(items) || items.length === 0) {
      return [];
    }

    return items.map((item) => {
      const status = this.mapStatus(item.requoteStatus);
      const historyTitle = status === 'Accepted' ? 'Request Accepted' : status === 'Pending' ? 'Request Initiated' : 'Request Declined';

      return {
        requestId: item.reviewerRequoteVersion || '-',
        status,
        history: [
          {
            title: historyTitle,
            quoteId: item.newQuoteId || '',
            dateTime: item.createdOn,
            reasonLabel: 'Requote Reason',
            reason: item.reason || '-',
            avatarUrl: 'assets/images/generic-agent.svg',
            actorName: item.requestedByReviewerName || '',
            actorRole: 'Reviewer',
          }
        ]
      };
    });
  }

  private mapStatus(status?: string): RequoteStatus {
    const normalized = (status || '').toLowerCase().trim();
    if (normalized === 'accepted') return 'Accepted';
    if (normalized === 'pending') return 'Pending';
    return 'Declined';
  }

  onTabChange(event: { index: number }): void {
    this.selectedTabIndex.set(event.index);
  }

  isRemarksTabSelected(): boolean {
    return this.selectedTabIndex() === 0;
  }

  isAcceptedStatus(status: RequoteStatus): boolean {
    return status === 'Accepted';
  }

  isPendingStatus(status: RequoteStatus): boolean {
    return status === 'Pending';
  }

  getBannerVariant(status: RequoteStatus): BannerVariant {
    if (status === 'Accepted') return 'success';
    if (status === 'Pending') return 'warning';
    return 'error';
  }

  formatTime(dateTime: string): string {
    if (!dateTime) return '';
    const dt = new Date(dateTime);

    return new Intl.DateTimeFormat('en-GB', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    }).format(dt);
  }

  formatDate(dateTime: string): string {
    if (!dateTime) return '';
    const dt = new Date(dateTime);
    if (Number.isNaN(dt.getTime())) {
      return dateTime;
    }

    return new Intl.DateTimeFormat('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    }).format(dt);
  }

  formatDateTime(dateTime: string): string {
    if (!dateTime) return '';
    return `${this.formatTime(dateTime)}, ${this.formatDate(dateTime)}`;
  }

  trackByRequoteRequestId(index: number, requote: { requestId: string }): string {
    return `${requote.requestId}-${index}`;
  }

  shouldShowPanelToggle(requote: RequoteItem): boolean {
    return requote.history.length > 0;
  }

  getPanelToggleIcon(isExpanded: boolean): string {
    return isExpanded ? '-' : '+';
  }
}
